import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { Box, Button, Container, Drawer, IconButton, Stack } from '@mui/material';
import MenuRounded from '@mui/icons-material/MenuRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import AndroidRounded from '@mui/icons-material/AndroidRounded';
import { tokens } from '../../theme';
import { APP_ICON, PLAY_URL } from '../ui/constants';

const NAV = [
  { text: 'Features', path: '/features' },
  { text: 'Security', path: '/security' },
  { text: 'Scanning', path: '/document-scanning' },
  { text: 'Backup', path: '/cloud-backup' },
  { text: 'Wear OS', path: '/wear-os' },
  { text: 'About', path: '/about' },
];

const LEGAL = [
  { text: 'Privacy policy', path: '/privacy-policy' },
  { text: 'Terms and conditions', path: '/terms-and-conditions' },
];

export const Brand: React.FC<{ light?: boolean }> = ({ light }) => (
  <Box
    component={RouterLink}
    to="/"
    aria-label="ID Card Wallet home"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1.25,
      textDecoration: 'none',
      color: light ? '#fff' : tokens.ink,
      borderRadius: '10px',
      '&:focus-visible': { outline: `3px solid ${tokens.foil}`, outlineOffset: 4 },
    }}
  >
    <Box
      component="img"
      src={APP_ICON}
      alt=""
      referrerPolicy="no-referrer"
      width={36}
      height={36}
      sx={{ borderRadius: '10px', display: 'block' }}
    />
    <Box component="span" sx={{ fontWeight: 700, fontSize: '1.08rem', letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>
      ID Card Wallet
    </Box>
  </Box>
);

const Navbar: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const isActive = (p: string) => location.pathname === p;

  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        bgcolor: scrolled ? 'rgba(242,244,247,0.86)' : tokens.paper,
        backdropFilter: scrolled ? 'saturate(160%) blur(14px)' : 'none',
        borderBottom: `1px solid ${scrolled ? tokens.line : 'transparent'}`,
        transition: 'background-color .25s, border-color .25s',
      }}
    >
      <Container maxWidth="lg">
        <Box component="nav" aria-label="Main" sx={{ height: { xs: 64, md: 72 }, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Brand />
          <Stack
            direction="row"
            component="ul"
            sx={{ listStyle: 'none', m: 0, p: 0, ml: 'auto', display: { xs: 'none', md: 'flex' }, gap: 0.5 }}
          >
            {NAV.map((item) => (
              <li key={item.path}>
                <Box
                  component={RouterLink}
                  to={item.path}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                  sx={{
                    position: 'relative',
                    display: 'inline-flex',
                    px: 1.5,
                    py: 1,
                    borderRadius: '10px',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    textDecoration: 'none',
                    color: isActive(item.path) ? tokens.ink : tokens.inkSoft,
                    '&:hover': { color: tokens.ink, bgcolor: tokens.paperDeep },
                    '&:focus-visible': { outline: `3px solid ${tokens.foil}`, outlineOffset: 2 },
                  }}
                >
                  {item.text}
                  {isActive(item.path) && (
                    <Box
                      component={motion.span}
                      layoutId="nav-underline"
                      sx={{ position: 'absolute', left: 12, right: 12, bottom: 2, height: 2, borderRadius: '2px', bgcolor: tokens.foil }}
                    />
                  )}
                </Box>
              </li>
            ))}
          </Stack>
          <Button
            variant="contained"
            href={PLAY_URL}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<AndroidRounded />}
            sx={{ display: { xs: 'none', md: 'inline-flex' }, ml: 1, minHeight: 42 }}
          >
            Get the app
          </Button>
          <IconButton
            aria-label="Open menu"
            aria-expanded={open}
            onClick={() => setOpen(true)}
            sx={{ ml: 'auto', display: { xs: 'inline-flex', md: 'none' }, color: tokens.ink, border: `1px solid ${tokens.line}` }}
          >
            <MenuRounded />
          </IconButton>
        </Box>
      </Container>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: 'min(88vw, 360px)', bgcolor: tokens.paper, p: 2.5 } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Brand />
          <IconButton aria-label="Close menu" onClick={() => setOpen(false)} sx={{ color: tokens.ink }}>
            <CloseRounded />
          </IconButton>
        </Box>
        <AnimatePresence>
          {open && (
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {[{ text: 'Home', path: '/' }, ...NAV].map((item, i) => (
                <motion.li
                  key={item.path}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.04 * i, duration: 0.3 }}
                >
                  <Box
                    component={RouterLink}
                    to={item.path}
                    aria-current={isActive(item.path) ? 'page' : undefined}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 1.6,
                      px: 1,
                      borderBottom: `1px solid ${tokens.line}`,
                      fontSize: '1.15rem',
                      fontWeight: 650,
                      textDecoration: 'none',
                      color: tokens.ink,
                      '&:focus-visible': { outline: `3px solid ${tokens.foil}`, outlineOffset: 2, borderRadius: '8px' },
                    }}
                  >
                    {item.text}
                    {isActive(item.path) && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tokens.foil }} />}
                  </Box>
                </motion.li>
              ))}
            </Box>
          )}
        </AnimatePresence>
        <Button
          variant="contained"
          fullWidth
          href={PLAY_URL}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<AndroidRounded />}
          sx={{ mt: 3 }}
        >
          Get it on Google Play
        </Button>
        <Stack direction="row" spacing={2} sx={{ mt: 'auto', pt: 3 }}>
          {LEGAL.map((l) => (
            <Box key={l.path} component={RouterLink} to={l.path} sx={{ color: tokens.inkSoft, fontSize: '0.88rem' }}>
              {l.text}
            </Box>
          ))}
        </Stack>
      </Drawer>
    </Box>
  );
};

export default Navbar;
